import {
	ConditionalObject,
	Do,
	J2rText,
	j2r,
	j2rButtonResponseSetAdvanced,
	timer,
	trionlineAjax,
} from '../components'
import { React, _ } from '../lib'

export const openResetPassword = () => ({
	cappedWidth: true,
	hideSidebar: true,
	theme: 'green',
	content: () => ({ tag: PageResetPassword }),
})

class PageResetPassword extends React.Component<any, any> {
	constructor(props) {
		super(props)
		this.state = {
			// Form state - stage
			stage: 1,
			// Form state - entered
			email: '',
			resetCode: '',
			newPassword1: '',
			newPassword2: '',
			// Form state - loading / error
			isLoading: false,
			message: '',
			pwdErrors: [],
			success: false,
		}
	}

	render() {
		return j2r(() => ({
			cl: Do(() => {
				let C = 'reset-password-wrapper'
				if (this.state.success) {
					C += ' success'
				}
				return C
			}),
			children: [
				// Heading to show at the top of the page
				{
					tag: 'h1',
					key: 'heading',
					text: 'Set a New Password',
				},

				// Small reminder not to refresh the page
				{
					tag: 'p',
					key: 'bold-item',
					cl: 'bold-item',
					text: 'Please keep this browser tab open until you have completed the reset process',
				},

				// Help link
				{
					tag: 'p',
					key: 'help-link-wrapper',
					children: [
						{
							tag: 'a',
							key: 'help-link',
							href: '/kb/335/',
							text: 'Need help?',
						},
					],
				},

				// Stages 1, 2, and 3
				this.state.stage < 3 ? this.buildStep1() : undefined,
				this.state.stage === 2 ? this.buildStep2() : undefined,
				this.state.stage === 3 ? this.buildStep3() : undefined,

				// Display help information at the bottom
				this.buildHelpSection(),
			],
		}))
	}

	buildStep1() {
		return {
			tag: 'form',
			key: 'step-1',
			cl: 'step step-1',
			children: [
				// Section heading and description
				{
					key: 'heading',
					cl: 'heading step-1',
					text: 'Email Address',
				},
				{
					tag: 'p',
					key: 'help',
					children: [
						`\
Tell us your email address to get started. \
If it isn't associated with an account, you can\
`,
						{
							tag: 'a',
							key: 'link',
							href: '/#request',
							text: ' request an account',
						},
						'.',
					],
				},

				// Input box
				{
					tag: J2rText,
					key: 'email-input',
					cl: 'txtEmail cntr-input-wrapper cntr-input',
					autoFocus: true,
					placeholder: 'E.g. john@softwarenorth.com.au',
					disabled: this.state.stage > 1 || this.state.isLoading,
					fixer: v => v.trim().toLowerCase(),
					value: this.state.email,
					onUpdate: v => {
						this.setState({ email: v })
					},
				},

				// Show the button if it's in stage 1
				ConditionalObject(
					this.state.stage === 1,
					j2rButtonResponseSetAdvanced({
						loading: this.state.isLoading,
						message: this.state.message,
						buttons: [
							{
								label: 'Get Reset Code',
								type: 'submit',
								onClick: _.noop,
							},
						],
					}),
				),
			],
			onSubmit: e => {
				e.preventDefault()
				this.submitStep1()
			},
		}
	}

	submitStep1() {
		// Start loading
		this.setState({
			isLoading: true,
			message: '',
		})

		// First validate that something was entered
		if (!this.state.email) {
			this.setState({
				isLoading: false,
				message: 'Please enter an email address.',
			})
			return
		}

		// Send server request
		trionlineAjax({
			url: './req/',
			method: 'POST',
			data: {
				e: this.state.email,
			},
			yes: data => {
				// Get the error message, if there is one
				const error = Do(() => {
					switch (data) {
						case '':
							return 'Unexpected error. Failed to send email.'
						case 'none':
							return 'There is no TriOnline account with that email address.'
						case 'disabled':
							return 'This account has not been enabled. Please speak to your administrator about enabling this account.'
						case 'disabled':
							return 'This account is not allowed to reset its TriOnline password.'
						default:
							return null
					}
				})

				// If there was an error, exit early
				// Otherwise, being stage 2
				if (error) {
					this.setState({ message: error })
				} else {
					this.setState({ stage: 2 })
				}
				this.setState({ isLoading: false })
			},
		})
	}

	buildStep2() {
		return {
			tag: 'form',
			key: 'step-2',
			cl: 'step step-2',
			children: [
				// Section heading and description
				{
					key: 'heading',
					cl: 'heading step-2',
					text: 'Reset Code',
				},
				{
					tag: 'p',
					key: 'help',
					cl: 'help',
					text: `\
A reset code has been sent to your email address. Copy and paste the \
code in the box to the right. (Note: check your junk/spam folder if \
you can't find it)\
`,
				},

				// Input - hide if no longer in stage 2
				ConditionalObject(this.state.stage <= 2, {
					tag: J2rText,
					key: 'code-input',
					cl: 'txtResetCode cntr-input-wrapper cntr-input',
					autoFocus: true,
					maxLength: 8,
					placeholder: 'E.g. 47bf937e',
					disabled: this.state.stage > 2 || this.state.isLoading,
					value: this.state.resetCode,
					onUpdate: v => {
						this.setState({ resetCode: v })
					},
				}),

				// Show the button if it's in stage 2
				ConditionalObject(
					this.state.stage === 2,
					j2rButtonResponseSetAdvanced({
						loading: this.state.isLoading,
						message: this.state.message,
						buttons: [
							{
								label: 'Validate Email Address',
								type: 'submit',
								onClick: _.noop,
							},
						],
					}),
				),
			],
			onSubmit: e => {
				e.preventDefault()
				this.submitStep2()
			},
		}
	}

	submitStep2() {
		// Start loading
		this.setState({
			isLoading: true,
			message: '',
		})

		// First validate that something was entered
		if (!this.state.resetCode) {
			this.setState({
				isLoading: false,
				message: 'Please enter a reset code.',
			})
			return
		}

		// Send server request
		trionlineAjax({
			url: './req/',
			method: 'POST',
			data: {
				e: this.state.email,
				r: this.state.resetCode,
			},
			yes: data => {
				// Display error as needed
				// Otherwise, proceed to stage 3
				if (data !== '1') {
					this.setState({ message: 'This reset code is incorrect.' })
				} else {
					this.setState({ stage: 3 })
				}
				this.setState({ isLoading: false })
			},
		})
	}

	buildStep3() {
		return {
			tag: 'form',
			key: 'step-3',
			cl: 'step step-3',
			children: [
				// Heading and description
				{
					key: 'heading',
					cl: 'heading step-3',
					text: 'New Password',
				},
				{
					tag: 'p',
					key: 'help',
					cl: 'help',
					text: `\
Identify confirmed. You may now enter a new password. \
Try to make it something easy to remember but difficult to guess. \
For better security we recommend a password manager such as LastPass \
to generate a random password you don't need to memorise.\
`,
				},
				{
					tag: 'p',
					key: 'hint',
					cl: 'help',
					text: 'Note: Passwords must be at least eight (8) characters long.',
				},

				// Hidden disabled input with the username for password managers
				{
					tag: 'label',
					key: 'email',
					cl: 'pwdFlex cntr-input-wrapper',
					children: [
						{
							tag: 'span',
							key: 'lbl',
							text: 'Email',
						},
						{
							tag: J2rText,
							key: 'pwd-input',
							cl: 'tb2 txtPassword1 cntr-input',
							autoComplete: 'email',
							readOnly: true,
							value: this.state.email,
						},
					],
				},

				// Inputs
				{
					tag: 'label',
					key: 'password-1',
					cl: 'pwdFlex cntr-input-wrapper',
					children: [
						{
							tag: 'span',
							key: 'lbl',
							text: 'New Password',
						},
						{
							tag: J2rText,
							key: 'pwd-input',
							cl: 'tb2 txtPassword1 cntr-input',
							type: 'password',
							autoComplete: 'new-password',
							value: this.state.newPassword1,
							onUpdate: v => {
								this.setState({ newPassword1: v })
							},
						},
					],
				},
				{
					tag: 'label',
					key: 'password-2',
					cl: 'pwdFlex cntr-input-wrapper',
					children: [
						{
							tag: 'span',
							key: 'lbl',
							text: 'Confirm',
						},
						{
							tag: J2rText,
							key: 'pwd-input',
							cl: 'tb2 txtPassword2 cntr-input',
							type: 'password',
							autoComplete: 'new-password',
							value: this.state.newPassword2,
							onUpdate: v => {
								this.setState({ newPassword2: v })
							},
						},
					],
				},

				// Show the button if it's in stage 3
				ConditionalObject(
					this.state.stage === 3,
					j2rButtonResponseSetAdvanced({
						loading: this.state.isLoading,
						message: this.state.message,
						buttons: [
							{
								label: 'Set New Password',
								type: 'submit',
								onClick: _.noop,
							},
						],
					}),
				),

				// Show password errors
				ConditionalObject(this.state.pwdErrors.length > 0, {
					tag: 'ul',
					key: 'password-errors',
					cl: 'password-errors',
					children: this.state.pwdErrors.map((x, i) => ({
						tag: 'li',
						key: i,
						text: x,
					})),
				}),
			],
			onSubmit: e => {
				e.preventDefault()
				this.submitStep3()
			},
		}
	}

	submitStep3() {
		// Start loading
		this.setState({
			isLoading: true,
			message: '',
			pwdErrors: [],
		})

		// First validate that something was entered
		if (!this.state.newPassword1 || !this.state.newPassword2) {
			this.setState({
				isLoading: false,
				message: 'Please fill both boxes with your new password.',
			})
			return
		}
		if (this.state.newPassword1 !== this.state.newPassword2) {
			this.setState({
				isLoading: false,
				message: 'The two passwords you have entered do not match.',
			})
			return
		}

		// Send server request
		trionlineAjax({
			url: './req/',
			method: 'POST',
			data: {
				email: this.state.email,
				reset: this.state.resetCode,
				p1: this.state.newPassword1,
				p2: this.state.newPassword2,
			},
			yes: data => {
				// If there's an error, display it
				// Otherwise, send the user back to the account page
				if (data !== '1') {
					if (data[0] !== '[') {
						this.setState({ message: data })
					} else {
						this.setState({ pwdErrors: JSON.parse(data) })
					}
				} else {
					this.setState({
						message: 'Reset Successful! Redirecting...',
						success: true,
					})
					timer(2000, () => {
						location.href = '../'
					})
				}
				this.setState({ isLoading: false })
			},
		})
	}

	buildHelpSection() {
		return {
			cl: 'help-section',
			key: 'help-section',
			children: [
				{
					cl: 'heading help',
					key: 'heading',
					text: 'For Assistance',
				},
				{
					tag: 'ul',
					key: 'list',
					children: [
						{
							tag: 'li',
							key: 1,
							text: 'Contact your Facility Administrator.',
						},
						{
							tag: 'li',
							key: 2,
							children: [
								{
									tag: 'a',
									key: 'link',
									href: 'https://www.youtube.com/watch?v=8vFl9lxyGts',
									target: '_blank',
									text: 'YouTube Link',
								},
							],
						},
					],
				},
			],
		}
	}
}
